import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { searchMovements } from '../../api/business/endpoints';
import type { MovementSortKey, StockMovementType } from '../../api/business/types';
import { CompanySelector } from '../../components/CompanySelector';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { STOCK_MOVEMENT_TYPES } from '../../components/stockMovementTypes';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { useSort } from '../../components/useSort';
import { batchLabel } from '../../components/batchLabel';
import { useLanguage } from '../../i18n/LanguageContext';

/** The ledger: every quantity change, newest first, server-paginated (US-11). `?productId=` narrows it. */
export const MovementsPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;
  const selection = useSelectedCompany('stock.read');
  const company = selection.company;
  const [type, setType] = useState<StockMovementType | ''>('');
  const [page, setPage] = useState(0);
  const sort = useSort<MovementSortKey>('createdAt', 'desc', () => setPage(0));

  const query = useQuery({
    queryKey: ['movements', company?.id, productId, type, page, sort.key, sort.direction],
    enabled: company !== null,
    queryFn: () =>
      searchMovements(company?.id ?? '', {
        productId,
        type: type || undefined,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('movements.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('movements.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('movements.title')}
        subtitle={t('movements.subtitle')}
        action={
          <CompanySelector
            companies={selection.companies}
            value={company}
            onChange={(companyId) => {
              selection.select(companyId);
              setPage(0);
            }}
          />
        }
      />
      <ErrorNotice error={query.error ?? selection.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            select
            size="small"
            label={t('field.movementType')}
            value={type}
            onChange={(event) => {
              setType(event.target.value as StockMovementType | '');
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {STOCK_MOVEMENT_TYPES.map((one) => (
              <MenuItem key={one} value={one}>
                {t(`movementType.${one}` as const)}
              </MenuItem>
            ))}
          </TextField>
          {productId ? (
            <Chip
              label={t('movements.oneProduct')}
              onDelete={() => {
                setSearchParams({});
                setPage(0);
              }}
            />
          ) : null}
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="createdAt">
                  {t('movements.when')}
                </SortableCell>
                <TableCell>{t('field.productName')}</TableCell>
                <TableCell>{t('field.movementType')}</TableCell>
                <SortableCell sort={sort} column="quantityDelta" align="right">
                  {t('movements.delta')}
                </SortableCell>
                <TableCell align="right">{t('movements.after')}</TableCell>
                <TableCell>{t('field.reference')}</TableCell>
                <TableCell>{t('movements.who')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading || selection.isLoading ? (
                <TableSkeletonRows columns={7} />
              ) : (
                rows.map((movement) => (
                  <TableRow
                    key={movement.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${company?.id ?? ''}/${movement.productId}`)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(movement.createdAt, language)}
                    </TableCell>
                    <TableCell>
                      {movement.productName}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {batchLabel(movement, t, language)}
                        {movement.packName !== null
                          ? ` · ${t('movements.inPack', { name: movement.packName })}`
                          : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`movementType.${movement.type}` as const)}
                      />
                      {movement.reason !== null ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {t(`movementReason.${movement.reason}` as const)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: movement.quantityDelta > 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {movement.quantityDelta > 0 ? '+' : ''}
                      {formatNumber(movement.quantityDelta, language)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(movement.onHandAfter, language)}</TableCell>
                    <TableCell>
                      <Stack>
                        <Box>{movement.reference ?? t('common.none')}</Box>
                        {movement.note ? (
                          <Typography variant="caption" color="text.secondary">
                            {movement.note}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {movement.actorEmail ?? t('common.none')}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t(`movementSource.${movement.source}` as const)}
                        {movement.integrationLinkName ? ` · ${movement.integrationLinkName}` : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && company !== null && rows.length === 0 ? (
          <EmptyState title={t('movements.empty')} detail={t('movements.emptyDetail')} />
        ) : null}
        <ServerPagination
          page={page}
          pageSize={query.data?.pageSize ?? 25}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
        />
      </Paper>
    </>
  );
};
